// Small building blocks of the Marketing pages.
import { useTranslate } from "ra-core";
import type { CSSProperties, ReactNode } from "react";

import { PlatformBadge } from "@/components/competitors/PlatformIcon";
import type { Sale } from "@/components/atomic-crm/types";
import { UserAvatar } from "@/components/atomic-crm/misc/UserAvatar";
import { cn } from "@/lib/utils";

import { saleName } from "./hooks";
import { formatLabelKey, statusLabelKey, STATUS_COLOR } from "./constants";
import { pillarColor } from "./plans";
import type {
  ContentFormat,
  ContentPlanPillar,
  ContentStatus,
  MarketingChannel,
} from "./types";

export const StatusBadge = ({ status }: { status: ContentStatus }) => {
  const translate = useTranslate();
  return (
    <span className="inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full bg-muted px-2.5 text-xs font-semibold">
      <span
        aria-hidden
        className="size-1.5 rounded-full"
        style={{ backgroundColor: STATUS_COLOR[status] ?? STATUS_COLOR.idea }}
      />
      <span className="sr-only">{translate("marketing.status.label")}: </span>
      {translate(statusLabelKey(status))}
    </span>
  );
};

export const FormatChip = ({ format }: { format: ContentFormat }) => {
  const translate = useTranslate();
  return (
    <span className="inline-flex h-6 shrink-0 items-center rounded-full bg-primary/8 px-2.5 text-xs font-semibold text-primary dark:bg-white/10 dark:text-[#bcd3ef]">
      {translate(formatLabelKey(format))}
    </span>
  );
};

/** "A" / "B" letters of a piece's pillars, in their colours. */
export const PillarChips = ({
  pillars,
  siblings,
  withName,
}: {
  pillars: readonly ContentPlanPillar[];
  siblings: readonly ContentPlanPillar[];
  withName?: boolean;
}) => (
  <>
    {pillars.map((pillar) => {
      const color = pillarColor(pillar, siblings);
      return (
        <span
          key={pillar.id}
          title={`${pillar.code} · ${pillar.name}`}
          className="mk-pillar"
          style={{ "--mk-pillar": color } as CSSProperties}
        >
          <span aria-hidden className="mk-pillar__code">
            {pillar.code}
          </span>
          {withName ? (
            <span className="truncate">{pillar.name}</span>
          ) : (
            <span className="sr-only">{pillar.name}</span>
          )}
        </span>
      );
    })}
  </>
);

export const ChannelLabel = ({
  channel,
  className,
}: {
  channel: MarketingChannel | undefined;
  className?: string;
}) => {
  const translate = useTranslate();
  if (!channel)
    return (
      <span className={cn("text-xs text-muted-foreground", className)}>
        {translate("marketing.common.no_channel")}
      </span>
    );
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5", className)}>
      <PlatformBadge platform={channel.platform} size="sm" />
      <span className="truncate text-xs font-medium text-muted-foreground">
        {channel.name}
      </span>
    </span>
  );
};

export const SaleAvatar = ({
  sale,
  className,
}: {
  sale: Sale | undefined;
  className?: string;
}) =>
  sale ? (
    <span title={saleName(sale)} className="inline-flex shrink-0">
      <UserAvatar
        src={sale.avatar?.src}
        name={saleName(sale)}
        className={cn("size-6 text-[10px]", className)}
      />
      <span className="sr-only">{saleName(sale)}</span>
    </span>
  ) : null;

/** A KPI tile of the summary / analytics. */
export const KpiTile = ({
  label,
  value,
  hint,
  tone,
  index = 0,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "brand";
  index?: number;
}) => (
  <div
    className={cn(
      "mi-card mi-rise flex min-w-0 flex-col gap-1 p-3.5 md:p-4",
      tone === "brand" && "mk-kpi-brand",
    )}
    style={{ "--mi-index": index } as CSSProperties}
  >
    <span className="text-xs leading-tight font-medium text-muted-foreground">
      {label}
    </span>
    <span className="text-2xl leading-none font-semibold tracking-tight tabular-nums">
      {value}
    </span>
    {hint ? (
      <span className="text-xs text-muted-foreground">{hint}</span>
    ) : null}
  </div>
);

export interface BarDatum {
  key: string;
  label: ReactNode;
  value: number;
  /** Text shown at the end of the bar (defaults to the value). */
  display?: ReactNode;
  color?: string;
}

/**
 * Horizontal CSS bars (accessible list: each row reads "label: value"). No
 * chart library: the values are few and this stays crisp in both themes.
 */
export const BarList = ({
  data,
  label,
}: {
  data: readonly BarDatum[];
  label: string;
}) => {
  const max = Math.max(1, ...data.map((entry) => entry.value));
  return (
    <ul aria-label={label} className="flex flex-col gap-2.5">
      {data.map((entry) => (
        <li key={entry.key} className="min-w-0">
          <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
            <span className="min-w-0 truncate font-medium">{entry.label}</span>
            <span className="shrink-0 text-xs font-semibold text-muted-foreground tabular-nums">
              {entry.display ?? entry.value}
            </span>
          </div>
          <div className="mk-bar" aria-hidden>
            <span
              className="mk-bar__fill"
              style={
                {
                  "--mk-bar": `${(entry.value / max) * 100}%`,
                  ...(entry.color ? { background: entry.color } : {}),
                } as CSSProperties
              }
            />
          </div>
        </li>
      ))}
    </ul>
  );
};
